import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useSupportContext } from '../../../context/SupportContext';
import axios from 'axios';
import {
  FileText,
  Gavel,
  Upload,
  MessageSquare,
  CheckSquare,
  ArrowRight,
  Clock,
  AlertCircle,
  CheckCircle2,
  Target,
  Eye,
} from 'lucide-react';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Managed Client Dashboard - Phase 6 + Phase 9.2 Support Context
 * 
 * View-focused dashboard for managed clients.
 * Shows case status and action items.
 * 
 * Phase 9.2: Context-aware greetings and text when Platform Owner
 * is viewing this dashboard in Support Mode.
 */
const ManagedClientDashboard = () => {
  const { token } = useAuth();
  const { supportContext, isInSupportMode } = useSupportContext();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Phase 9.2: Determine effective client identity for context-aware text
  const effectiveClientName = isInSupportMode 
    ? (supportContext?.effective_user_name || 'Client')
    : null;
  
  const isViewingAsSupport = isInSupportMode && supportContext?.effective_user_id;

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await axios.get(`${API}/portal/managed/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDashboard(response.data);
      } catch (err) {
        console.error('Failed to fetch managed client dashboard:', err);
        setError(err.response?.data?.detail || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchDashboard();
    }
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  if (!dashboard?.has_case) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="text-emerald-600" size={32} />
        </div>
        {/* Phase 9.2: Context-aware empty state */}
        {isViewingAsSupport ? (
          <>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Eye size={16} className="text-emerald-600" />
              <h2 className="text-xl font-semibold text-stone-900">Viewing: {effectiveClientName}</h2>
            </div>
            <p className="text-slate-500 max-w-md mx-auto">
              {dashboard?.message || 'No active case found for this client. A case is being set up by their specialist.'}
            </p>
          </>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">Welcome!</h2>
            <p className="text-slate-500 max-w-md mx-auto">
              {dashboard?.message || 'Your case is being set up. Your specialist will reach out soon.'}
            </p>
          </>
        )}
      </div>
    );
  }

  const { case: caseData, credit_scores, progress, your_action_items, communication, recent_updates, your_specialist, capabilities } = dashboard;

  return (
    <div className="space-y-6" data-testid="managed-client-dashboard">
      {/* Status Banner - Phase 9.2 Context-Aware */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            {isViewingAsSupport ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <Eye size={18} className="text-emerald-200" />
                  <span className="text-sm text-emerald-200 font-medium">Support View</span>
                </div>
                <h1 className="text-2xl font-bold">Managed Client: {effectiveClientName}</h1>
              </>
            ) : (
              <h1 className="text-2xl font-bold">Your Credit Repair Progress</h1>
            )}
            <p className="text-emerald-100 mt-1">
              Case Status: <span className="font-semibold capitalize">{caseData?.status?.replace('_', ' ') || 'Active'}</span>
            </p>
          </div>
          {your_specialist && (
            <div className="bg-white/10 rounded-lg px-4 py-2">
              <p className="text-xs text-emerald-200">{isViewingAsSupport ? 'Assigned Specialist' : 'Your Specialist'}</p>
              <p className="font-semibold">{your_specialist.name || 'Assigned'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Action Items Alert - Phase 9.2 Context-Aware */}
      {(your_action_items?.document_requests > 0 || your_action_items?.tasks > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-4">
          <AlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={24} />
          <div>
            <h3 className="font-semibold text-amber-800">
              {isViewingAsSupport ? 'Client Action Required' : 'Action Required'}
            </h3>
            <p className="text-amber-700 text-sm mt-1">
              {your_action_items.document_requests > 0 && (
                <>{isViewingAsSupport ? `This client has` : `You have`} {your_action_items.document_requests} document request(s) pending. </>
              )}
              {your_action_items.tasks > 0 && (
                <>{isViewingAsSupport ? `${your_action_items.tasks} task(s) awaiting client action.` : `You have ${your_action_items.tasks} task(s) to complete.`}</>
              )}
            </p>
            <Link 
              to="/client/documents" 
              className="inline-flex items-center gap-1 text-amber-800 font-medium text-sm mt-2 hover:text-amber-900"
            >
              View requests <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )}

      {/* Credit Scores - Phase 9.2 Context-Aware */}
      {Object.keys(credit_scores || {}).length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-stone-900 mb-4">
            {isViewingAsSupport ? `${effectiveClientName}'s Credit Scores` : 'Your Credit Scores'}
          </h2>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(credit_scores).map(([bureau, score]) => (
              <div key={bureau} className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-stone-900">{score || '--'}</p>
                <p className="text-sm text-slate-500 capitalize mt-1">{bureau}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Gavel className="text-emerald-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Disputes In Progress</p>
              <p className="text-2xl font-bold text-stone-900">{progress?.disputes_in_progress || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="text-emerald-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Disputes Resolved</p>
              <p className="text-2xl font-bold text-stone-900">{progress?.disputes_resolved || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Link
          to="/client/documents"
          className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow flex items-center gap-4"
        >
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
            <Upload className="text-slate-600" size={20} />
          </div>
          <div className="flex-1">
            <p className="font-medium text-stone-900">Documents</p>
            <p className="text-sm text-slate-500">Upload & view</p>
          </div>
          <ArrowRight className="text-slate-400" size={20} />
        </Link>
        <Link
          to="/client/messages"
          className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow flex items-center gap-4"
        >
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
            <MessageSquare className="text-slate-600" size={20} />
          </div>
          <div className="flex-1">
            <p className="font-medium text-stone-900">Messages</p>
            {communication?.unread_messages > 0 && (
              <p className="text-sm text-emerald-600 font-medium">{communication.unread_messages} unread</p>
            )}
          </div>
          <ArrowRight className="text-slate-400" size={20} />
        </Link>
        <Link
          to="/client/disputes"
          className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow flex items-center gap-4"
        >
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
            <Gavel className="text-slate-600" size={20} />
          </div>
          <div className="flex-1">
            <p className="font-medium text-stone-900">Dispute Status</p>
            <p className="text-sm text-slate-500">Track progress</p>
          </div>
          <ArrowRight className="text-slate-400" size={20} />
        </Link>
      </div>

      {/* Recent Updates */}
      {recent_updates?.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-stone-900 mb-4">Recent Updates</h2>
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {recent_updates.slice(0, 5).map((event, index) => (
              <div key={index} className="p-4 flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Target size={16} className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-900">{event.title}</p>
                  {event.description && (
                    <p className="text-sm text-slate-500 truncate">{event.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  <Clock size={12} />
                  <span>{new Date(event.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* What Your Specialist Handles - Phase 9.2 Context-Aware */}
      <div className="bg-slate-50 rounded-xl p-6">
        <h3 className="font-semibold text-stone-900 mb-3">
          {isViewingAsSupport ? 'Services Being Provided' : 'What Your Specialist Handles'}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-600">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-500" />
            <span>{isViewingAsSupport ? 'Credit report analysis' : 'Analyzing your credit reports'}</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-500" />
            <span>{isViewingAsSupport ? 'Dispute item selection' : 'Selecting items to dispute'}</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-500" />
            <span>{isViewingAsSupport ? 'Dispute letter creation & sending' : 'Creating and sending dispute letters'}</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-500" />
            <span>{isViewingAsSupport ? 'Response tracking' : 'Tracking dispute responses'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagedClientDashboard;
